<template>
  <base-dialog
    width="500px"
    data-testid="statechartPropertiesDialog.dialog"
    :title="title"
    dialog-id="statechartPropertiesDialog"
    @complete-dialog="loading || mode !== 'create' || !saveEnabled ? () => {} : onSave()"
    @cancel-dialog="onCancel"
  >
    <template #content>
      <v-text-field
        ref="titleRef"
        v-model="finalTitleTemp"
        :disabled="loading || mode === 'info'"
        class="title-dialog-text-field mb-5"
        data-testid="statechartPropertiesDialog.title-input"
        :label="mode !== 'create' ? 'Statechart Title' : 'New Statechart Title'"
        variant="outlined"
        density="compact"
        :messages="uniqueName ? [] : 'Warning: One of your Statecharts already has this title.'"
        @update:focused="
          (focused) => {
            if (!focused) {
              finalTitle = validateStatechartName(finalTitleTemp);
              finalTitleTemp = finalTitle;
            }
          }
        "
      >
        <template #append-inner>
          <v-progress-circular
            v-if="loading"
            data-testid="statechartPropertiesDialog.loading-spinner"
            indeterminate
            color="primary"
          >
          </v-progress-circular> </template
      ></v-text-field>
      <v-textarea
        v-if="mode !== 'create'"
        v-model="finalDescription"
        :disabled="mode === 'info'"
        label="Description"
        variant="outlined"
        density="compact"
        hide-details
        data-testid="statechartPropertiesDialog.description-input"
      ></v-textarea>
    </template>
    <template #footer>
      <v-btn-dialog-action data-testid="statechartPropertiesDialog.cancel-button" @click="onCancel"
        >Cancel</v-btn-dialog-action
      >
      <v-btn-dialog-action-CTA
        v-if="mode !== 'info'"
        data-testid="statechartPropertiesDialog.save-button"
        :disabled="loading || !saveEnabled"
        @click="onSave()"
        >{{ mode === "edit" ? "Save" : "Create" }}</v-btn-dialog-action-CTA
      >
    </template>
  </base-dialog>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import BaseDialog from "../../shared/BaseDialog.vue";
import { default as empty } from "../models/empty.json";
import { untitledStatechart, validateStatechartName } from "../utils/validateStatechartName";
import { ModelData } from "../services/statecharts/ModelData";
import { StatechartsService } from "../services/statecharts/statecharts-service";
import { numberedProposal } from "./utils/numberedProposal";
import { DirtyState } from "../services/dirty-state";
import { Subscription } from "rxjs/internal/Subscription";

const compProps = withDefaults(
  defineProps<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    newModel: ModelData | any;
    mode?: "create" | "edit" | "info";
  }>(),
  { mode: "create" }
);
const emit = defineEmits(["cancel", "save"]);
const newModelJson = ref(empty);
const statechartType = ref<"example" | "blank" | "duplicate" | "edit" | "info">("blank");
const loading = ref(false);
const currentModelNames = ref<string[]>([]);
const titleRef = ref(null);

const finalTitle = ref<string>("");
const finalTitleTemp = ref<string>("");
const finalDescription = ref<string>("");

const isDirty = ref<boolean>(true);

const cloudStatechartService: StatechartsService = inject<StatechartsService>("cloudStatechartService");

const dirtyState: DirtyState = inject<DirtyState>("dirtyState");

const dirtyStateSub = ref<Subscription>();

const setDirtyState = (value: boolean) => (isDirty.value = value);

onMounted(() => {
  dirtyStateSub.value = dirtyState.observable.subscribe(setDirtyState);
});

onBeforeUnmount(() => {
  dirtyStateSub.value.unsubscribe();
});

watch(
  () => compProps.newModel,
  async (newVal) => {
    if (newVal) {
      if (compProps.mode === "edit") {
        statechartType.value = "edit";
      } else if (compProps.mode === "info") {
        statechartType.value = "info";
      } else if ("svgUrl" in newVal) {
        statechartType.value = "duplicate";
      } else {
        statechartType.value = "example";
        newModelJson.value = newVal;
      }
    } else {
      statechartType.value = "blank";
      newModelJson.value = empty;
    }

    loading.value = true;
    setCurrentModels(newVal);
  },
  {
    immediate: true,
  }
);

async function setCurrentModels(newVal) {
  await cloudStatechartService
    .getModels()
    .then((res: ModelData[]) => {
      currentModelNames.value = res.map((el) => {
        return el.title;
      });
      initFields(newVal, currentModelNames.value);
      loading.value = false;
      nextTick(() => {
        titleRef.value?.focus();
      });
    })
    .catch(() => {
      initFields(newVal, []);

      loading.value = false;
      nextTick(() => {
        titleRef.value?.focus();
      });
    });
}

watch(
  finalTitle,
  function (value: string) {
    if (value === "") {
      value = untitledStatechart;
      finalTitle.value = value;
    }
    finalTitleTemp.value = value;
  },
  {
    immediate: true,
  }
);

const uniqueName = computed(() => {
  if ((compProps.mode === "edit" && finalTitleTemp.value === compProps.newModel.title) || compProps.mode === "info") {
    return true;
  }
  return !currentModelNames.value.includes(finalTitleTemp.value);
});

const title = computed(() => {
  let val: string;
  switch (statechartType.value) {
    case "edit":
      val = "Edit Statechart Details";
      break;
    case "info":
      val = "Statechart Details";
      break;
    case "blank":
      val = "Create new Statechart";
      break;
    case "example":
      val = "Create new Statechart from Example";
      break;
    case "duplicate":
      val = "Duplicate Statechart";
      break;
  }
  return val;
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function initFields(newVal: any | ModelData, currentModelNames: string[]) {
  let proposal: string;
  switch (statechartType.value) {
    case "edit":
    case "info":
      finalTitle.value = newVal.title;
      finalDescription.value = newVal.description;
      return;
    case "blank":
      proposal = "My Statechart";
      break;
    case "example":
      proposal = newVal?.cells[0].attrs.name.text ?? "";
      break;
    case "duplicate":
      proposal = newVal.title + " Copy";
      break;
  }

  finalTitle.value = numberedProposal(currentModelNames, proposal);
}

function onCancel() {
  emit("cancel");
}

const saveEnabled = computed(() => {
  if (compProps.mode === "edit") {
    if (
      finalTitle.value === compProps.newModel?.title &&
      (finalDescription.value === compProps.newModel?.description ||
        (compProps.newModel?.description === null && finalDescription.value === ""))
    ) {
      return false;
    }
  }
  return true;
});

function onSave() {
  finalTitle.value = validateStatechartName(finalTitleTemp.value);
  finalTitleTemp.value = finalTitle.value;
  if (compProps.mode === "create") {
    if (compProps.newModel && "svgUrl" in compProps.newModel) {
      emit("save", finalTitle.value);
    } else {
      newModelJson.value.cells[0].attrs.name.text = finalTitle.value;
      emit("save", newModelJson.value);
    }
  } else {
    let title = undefined;
    let description = undefined;

    if (finalTitle.value !== compProps.newModel?.title) {
      title = finalTitle.value;
    }
    if (finalDescription.value !== compProps.newModel?.description) {
      description = finalDescription.value;
    }

    emit("save", {
      newTitle: title,
      newDescription: description,
    });
  }
}
</script>

<style>
.title-dialog-text-field .v-field__append-inner {
  align-items: center;
  padding-top: 0;
}

.title-dialog-text-field .v-messages__message {
  color: rgba(var(--v-theme-warning));
}
</style>
